import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, XCircleIcon, XOctagon, XSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { Close } from "@radix-ui/react-toast";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80", // Suppression des animations
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideCloseButton?: boolean;
  }
>(({ className, children, hideCloseButton = false, ...props }, ref) => {
  const isMobile = useIsMobile();

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        onOpenAutoFocus={(e) => e.preventDefault()} 
        className={cn(
          // 👇 AJOUT de "focus:outline-none" ici pour enlever le contour bleu sur la modal
          "fixed z-50 grid gap-4 shadow-lg focus:outline-none", 
          isMobile
            ? "inset-0 w-full max-h-[100dvh] h-[100dvh] bg-background"
            : "left-[50%] top-[50%] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] rounded-lg border bg-background p-5 border-2 border-white/80 shadow-lg",
          className
        )}
        {...props}
      >
        {!props['aria-labelledby'] && (
          <DialogPrimitive.Title className="sr-only">
            Dialog
          </DialogPrimitive.Title>
        )}

        {!hideCloseButton && (
          <DialogPrimitive.Close className={cn(
            // 👇 AJOUT de "focus:outline-none focus:ring-0" 
            // et on garde une outline légère uniquement pour le clavier via "focus-visible:ring-2"
            "absolute mt-3.5 text-gray-500 bg-white rounded-full p-1 transition-opacity hover:text-gray-800 focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none z-[60]",
            isMobile ? "right-2 mt-3 bg-muted" : "right-[-11px] top-[-24px]"
          )}>
            <XCircleIcon className="h-6 w-6"/>
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}

        <div className={cn(
          "flex flex-col",
          isMobile && "h-full w-full bg-background overflow-hidden"
        )}>
          {children}
        </div>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogContent.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const isMobile = useIsMobile();

  return (
    <div
      className={cn(
        "flex flex-col space-y-1 text-black-500 text-center sm:text-left rounded-md bg-blue-50 p-3 pt-4 mb-2",
        isMobile && "px-6 py-4 bg-gray-50 z-10 sticky top-0 border-b mt-6",
        className
      )}
      {...props}
    />
  );
};
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const isMobile = useIsMobile();

  return (
    <div
      className={cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
        isMobile && "sticky bottom-0 left-0 right-0 px-6 py-4 border-t mt-auto justify-center flex-row space-x-2 pb-8 z-10 rounded-t-3xl bg-gray-50 border-gray-200", // Augmentation du padding pour une meilleure visibilité
        className
      )}
      {...props}
    />
  );
};
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
